import React, { CSSProperties, forwardRef, MouseEvent, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { Company, Person } from "@mapmycustomers/shared/types/entity";
import type GeoAddress from "@mapmycustomers/shared/types/GeoAddress";
import { EntityPin } from "@mapmycustomers/shared/types/map";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import EntityChip from "@app/component/EntityChip";
import LocationMiniMap from "@app/component/field/LocationMiniMap";
import FieldGrid from "@app/component/FieldGrid";
import { filterPinnedFields } from "@app/component/FieldGrid/utils/util";
import MarkerEffect from "@app/enum/MarkerEffect";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import { getMapSettings } from "@app/store/iam";
import RecordPreviewConfigurationColumns from "@app/store/recordPreview/RecordPreviewConfigurationColumns";
import { getRecordPreviewConfigurationColumns } from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import { TimelineEventNewCompany, TimelineEventNewPerson } from "@app/types/events/TimelineEvent";
import getContrastColor from "@app/util/colors/getContrastColor";
import { getFormattedAddressForUi } from "@app/util/formatters";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";

import BaseEvent from "./BaseEvent";
import styles from "./RecordEvent.module.scss";
import getPinFillColor from "./utils/getPinFillColor";

const isEmptyGeoAddress = (address?: GeoAddress): boolean =>
  !(
    address?.address ||
    address?.countryCode ||
    address?.city ||
    address?.region ||
    address?.fullPostalCode
  );

export interface RecordEventProps {
  event: TimelineEventNewCompany | TimelineEventNewPerson;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {
  columns: RecordPreviewConfigurationColumns;
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onShowEntityView: (payload: EntityViewPayload) => void;
}

const NewCompanyOrPersonEvent = forwardRef<HTMLDivElement, Props>(
  ({ columns, event, expanded, mapSettings, onShowEntityView, onSizeChange, style }, ref) => {
    const intl = useIntl();

    const record = event.entity;

    const handleShowEntity = useCallback(
      (payload: EntityViewPayload, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        onShowEntityView(payload);
      },
      [onShowEntityView]
    );

    const entityPinStyleGetter = useMemo(() => {
      const styleGetter = defaultEntityStyleGetter({
        colorGetter: () => {
          // TODO: add color&shape when platform returns them
          const fillColor = getPinFillColor(record.entity);
          return {
            color: getContrastColor(fillColor) === "black" ? colors.slate : colors.white,
            fillColor,
          };
        },
        draggable: false,
        effect: MarkerEffect.SHADOW,
        icon: () => (mapSettings.showEntityIcons ? record.entity : undefined),
        markerSize: mapSettings.markerSize,
        // TODO: add color&shape when platform returns them
        // shape: () => record.shape ?? MarkerShape.SQUARE,
        shape: () => MarkerShape.SQUARE,
      });
      // FIXME: generalize defaultEntityStyleGetter so that we can use it with other types in addition to EntityPin
      // For now this is a fake call to style getter. We do not use given argument inside that style getter completely
      return () => styleGetter({} as EntityPin);
    }, [record, mapSettings]);

    return (
      <BaseEvent
        className={record.geoPoint ? styles.mapContainer : undefined}
        date={event.updatedAt}
        expanded={expanded}
        extra={
          !record.geoPoint ? undefined : (
            <LocationMiniMap
              className={styles.map}
              coordinates={record.geoPoint.coordinates}
              styleGetter={entityPinStyleGetter}
            />
          )
        }
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.newCompanyOrPerson",
            defaultMessage:
              "created {record}{recordAddress, select, false {} other { at {recordAddress}}}",
            description: "Title of the New Company/Person event item",
          },
          {
            record: (
              <EntityChip
                entityId={record.id}
                entityType={record.entity}
                inline
                name={record.name}
                onClick={handleShowEntity}
              />
            ),
            recordAddress: isEmptyGeoAddress(record.geoAddress) ? (
              false
            ) : (
              <Tooltip title={getFormattedAddressForUi(record.geoAddress)}>
                <span className={styles.address}>
                  {getFormattedAddressForUi(record.geoAddress)}
                </span>
              </Tooltip>
            ),
          }
        )}
        user={event.updatedBy}
      >
        {columns[record.entity].filter(filterPinnedFields).length ? (
          <FieldGrid
            className={styles.grid}
            columns={columns[record.entity]}
            // TODO: we lack platform support here. We're intended to support full record body here, but only a few fields are available in reality
            entity={record as unknown as Company | Person}
            showEmpty
          />
        ) : null}
      </BaseEvent>
    );
  }
);

const mapStateToProps = (state: RootState) => ({
  columns: getRecordPreviewConfigurationColumns(state),
  mapSettings: getMapSettings(state),
});

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  NewCompanyOrPersonEvent
);
